import React, {useMemo, useState} from 'react';
import './SourceSelection.scss';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../src//assets/images/radio-active-icon.svg';
import {ReactComponent as RadioInactiveIcon} from '../../../../src/assets/images/radio-inactive-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../src/assets/images/radio-enable-icon.svg';
import {t} from 'i18next';
import classNames from 'classnames';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {useGetSourceSelection} from '../../../hooks/documentPrinting/useDocumentPrinting';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {
  buildTree,
  getFirstParentWithChildren,
  getParentIds,
} from '../batchPrintUtils';
import {
  setAnalysisSelectedSkipFolder,
  setAnalysisTranslationSkipOrIgnoreDocuments,
  setExpandedParentIds,
  setSaveBatchPrint,
  setSelectedSource,
  setSelectedSourceActionType,
} from '../../../redux/slice/batchPrintSlice';
import {useDispatch, useSelector} from 'react-redux';

const radioOptions = [
  {
    value: 'search',
    label: t('batchPrint.sourceSelection.selectSearch'),
  },
  {
    value: 'tag',
    label: t('batchPrint.sourceSelection.selectTag'),
  },
  {
    value: 'folder',
    label: t('batchPrint.sourceSelection.selectFolder'),
  },
];
const SourceSelection = () => {
  const batchPrint = useSelector((state: any) => state.batchPrint);
  const [action, setAction] = useState(
    batchPrint?.selectedSourceActionType || 'search'
  );
  const [selectedItem, setSelectedItem] = useState<string>(
    batchPrint?.selectedSource || ''
  );
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  const [defaultExpandedParentIds, setDefaultExpandedParentIds] = useState<
    string[]
  >(batchPrint?.expandedParentIds || []);
  const dispatch = useDispatch();

  const {refetch: refetchGetSourceSelection, data: getSourceSelectionData} =
    useGetSourceSelection();

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAction = event.target.value;
    setAction(selectedAction);

    setSelectedItem('');
    dispatch(setSelectedSource(''));
    if (!getSourceSelectionData) {
      refetchGetSourceSelection();
    }
  };

  const handleSelectedItem = (value: string[] | null) => {
    if (Array.isArray(value) && value.length > 0 && !value[0].includes('g')) {
      setSelectedItem(value[0]);
      dispatch(setSelectedSource(value[0]));
      const parentIds = getParentIds(getDataList, value[0]);
      setDefaultExpandedParentIds(parentIds);
      dispatch(setExpandedParentIds(parentIds));
      dispatch(setSelectedSourceActionType(action));
      dispatch(setAnalysisSelectedSkipFolder(''));
      dispatch(setAnalysisTranslationSkipOrIgnoreDocuments([]));
      dispatch(setSaveBatchPrint({id: Number(value[0])}));
      const sourceContextType =
        action === 'search'
          ? 'SAVEDSEARCHES'
          : action === 'tag'
            ? 'TAGS'
            : 'FOLDERS';
      dispatch(setSaveBatchPrint({sourceContextTypeCode: sourceContextType}));
    }
  };

  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setSearchKeyword(searchInput.trim());
    }
  };

  const getDataList = useMemo((): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    const newTreeData =
      action === 'search'
        ? buildTree(getSourceSelectionData?.sourceSelectionSearchList || [])
        : action === 'tag'
          ? buildTree(getSourceSelectionData?.sourceSelectionTagList || [])
          : action === 'folder'
            ? buildTree(getSourceSelectionData?.sourceSelectionFolderList || [])
            : buildTree([]);
    if (batchPrint?.selectedSourceActionType === action) {
      setDefaultExpandedParentIds(batchPrint?.expandedParentIds);
    } else if (newTreeData && newTreeData.length > 0) {
      const firstParentWithChildren = getFirstParentWithChildren(
        newTreeData,
        null
      );
      setDefaultExpandedParentIds([firstParentWithChildren]);
    }
    return newTreeData;
  }, [getSourceSelectionData, action]);

  const isRadioEnabled = getDataList?.length > 0;

  return (
    <Box className="source-selection-container">
      <Box className="source-selection-header">
        <Typography
          className="typo-text subhead2_size"
          data-testid={`source-selection-title`}
          tabIndex={0}
        >
          {t('batchPrint.sourceSelection.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchPrint.sourceSelection.title')}
          closeTooltipOnEscPress={true}
          trigger={
            <InfoIcon
              fontSize="small"
              className="info-icon"
              data-testid="add-user-title-info"
              aria-label="info-icon"
              tabIndex={0}
            />
          }
        >
          <p tabIndex={0}>{t('batchPrint.sourceSelection.titleInfo')}</p>
        </ArrTooltipWrapper>
      </Box>
      <Box className="source-selection-body">
        <Box className="body-header">
          <Typography
            className="source-selection-body-text typo-text body2_size"
            data-testid={`source-selection-select-description`}
            tabIndex={0}
          >
            {t('batchPrint.sourceSelection.selectDescription')}
          </Typography>
          <Box>
            <RadioGroup row value={action} onChange={handleActionChange}>
              {radioOptions.map(({value, label}, index) => {
                const isSelected = action === value;

                return (
                  <FormControlLabel
                    data-testid={`source-selection-select-description-${value}`}
                    key={value}
                    value={value}
                    control={
                      <Radio
                        disabled={false}
                        slotProps={{
                          input: {
                            'data-testid': `radio-${value}`,
                            'tabIndex': action === value ? 0 : -1,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                        icon={
                          getDataList?.length > 0 ? (
                            <RadioEnableIcon />
                          ) : (
                            <RadioInactiveIcon />
                          )
                        }
                        checkedIcon={<RadioActiveIcon />}
                      />
                    }
                    label={
                      <span
                        className={classNames(
                          'bulk-radio-label typo-text body3_size',
                          {
                            disabled: !isRadioEnabled,
                            active: isSelected && isRadioEnabled,
                            inactive: !isSelected && isRadioEnabled,
                          }
                        )}
                        data-testid={`source-selection-label-${index}`}
                      >
                        {label}
                      </span>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>
        </Box>

        <Box className="tree-header">
          {action !== 'tag' ? (
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              className="search-input"
              placeholder={t(`batchPrint.sourceSelection.searchPlaceholder`)}
              aria-label={t(`batchPrint.sourceSelection.searchPlaceholder`)}
              dataTestId="source-selection-search-input"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (e.target.value.trim() === '') {
                  setSearchKeyword('');
                }
              }}
              onKeyDown={handleSearchKeyEnter}
              onClear={() => {
                setSearchInput('');
                setSearchKeyword('');
              }}
              type="search"
            />
          ) : (
            <Typography
              className="typo-text subhead4_size"
              data-testid={`source-selection-select-tags`}
            >
              {t('batchPrint.sourceSelection.selectTags')}
            </Typography>
          )}
        </Box>
        <Box className="tree-container">
          <ArrTreeView
            setSelectedItem={handleSelectedItem}
            itemsList={getDataList}
            selectedItem={selectedItem}
            handleNoResultMessage={t('common.noResultFound')}
            allowGlobalSelect={false}
            multipleSelection={false}
            showActions={false}
            isShowCountLabel={true}
            showTooltip
            searchKeyword={searchKeyword}
            defaultExpandedIds={defaultExpandedParentIds}
            isSingleSelectionOnCtrl={true}
            isBlockParentNodeSelection={true}
            expandOnSearch={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SourceSelection;
